/**
 * updateManager — detects new deployed versions and surfaces a "Reload" toast.
 *
 * Flow (Chrome, Edge, Firefox, Safari 16+):
 *   1. Register /service-worker.js
 *   2. On `updatefound`, wait for the new worker to hit state 'installed'.
 *      If there's already a controller (i.e. the page is currently controlled
 *      by an older SW), an UPDATE is available → dispatch `cs:update-available`.
 *   3. UpdateAvailableToast listens for that event and renders a banner with
 *      "Reload" + "Later".
 *   4. On "Reload": postMessage {type: 'skip-waiting'} to the waiting SW,
 *      then listen for `controllerchange` and do window.location.reload().
 *   5. Every 5 min and on tab re-focus we call `registration.update()` so
 *      long-lived tabs catch new deploys.
 */
let cachedRegistration = null;

function notifyUpdateAvailable() {
  // Give the bundle a beat to load listeners
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('cs:update-available'));
  }, 50);
}

function trackWorker(worker) {
  if (!worker) return;
  const onState = () => {
    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      // There IS a controller → this install is an update (not first-run).
      notifyUpdateAvailable();
      worker.removeEventListener('statechange', onState);
    }
  };
  worker.addEventListener('statechange', onState);
}

export function installUpdateManager() {
  if (!('serviceWorker' in navigator)) return;

  // Reload when the new SW takes control (triggered by our skip-waiting message)
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  const onLoad = async () => {
    try {
      const reg = await navigator.serviceWorker.register('/service-worker.js');
      cachedRegistration = reg;

      // Case A: a new worker is already waiting (tab was in bg during install)
      if (reg.waiting && navigator.serviceWorker.controller) {
        notifyUpdateAvailable();
      }

      // Case B: one is installing right now
      if (reg.installing) trackWorker(reg.installing);

      // Case C: one appears later
      reg.addEventListener('updatefound', () => trackWorker(reg.installing));

      // Poll for updates every 5 min + on tab focus so long-lived tabs catch deploys
      setInterval(() => { reg.update().catch(() => {}); }, 5 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') reg.update().catch(() => {});
      });
    } catch { /* SW unavailable — silently ignore */ }
  };

  // Register after `load` so SW install doesn't contend with initial page resources
  if (document.readyState === 'complete') onLoad();
  else window.addEventListener('load', onLoad);
}

/** Called by the toast's "Reload" button. */
export function applyUpdateAndReload() {
  const reg = cachedRegistration;
  if (reg?.waiting) {
    reg.waiting.postMessage({ type: 'skip-waiting' });
    // controllerchange handler above will reload
  } else {
    window.location.reload();
  }
}
